import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Cell, Text, Tooltip } from 'recharts';
import { DateTime, Duration } from 'luxon';

// @mui/material components
import Grid from '@mui/material/Grid';
import withStyles from '@mui/styles/withStyles';

import { setTopReferrersInfo } from '../store/slices/dashboardStatsSlice';
import {
  setCaseIntendedClientId,
  // setIntendedClientId,
} from '../store/slices/intendedInfoSlice';

import { createStdRequest, apiFetch } from '../utils/utils';

import DA from '../store/ReduxAccess';

import KSelect from './KSelect';
import KSpinner from './KSpinner';
import DateRangePicker from './DateRangePicker';

const localStyles = {
  bufferDiv: {
    display: 'inline-block',
  },
  chartHolderDiv: {
    width: '100%',
    height: '370px',
    padding: '12px',
    backgroundColor: 'white',
    overflowY: 'scroll',
  },
  notificationDiv: {
    display: 'inline-block',
    width: '50%',
    fontSize: '120%',
    fontWeight: 500,
    color: '#cc0000',
    textAlign: 'center',
    height: '240px',
    marginTop: '130px',
  },
};

const fittingStr = (str) => {
  if (str.length > 24) {
    return str.substring(0, 21) + '...';
  } else return str;
};

const CustomizedTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return <div></div>;
  } else {
    return null;
  }
};

class TopReferralFirmsChart extends React.Component {
  combineClientsAndContacts = true;
  rawStats = [];

  constructor(props) {
    super(props);

    this.state = {
      dataReady: false,
      // timeRange: '',
      firstDate: '',
      lastDate: '',
      maxNToShow: 5,
      hoverLineIndex: -1,
      x: false,
    };
  }

  scrollTop = 0;
  refereesPerFirm = [];

  componentDidMount() {
    this.setState({ redirectToReferee: false });
    const { firstDate, lastDate } = this.determineBestTimeRange();
    const maxNToShow = this.determineBestMaxNToShow();
    this.setState({ firstDate, lastDate, maxNToShow }, () => this.fetchRawReferralStats());
  }

  handleScroll(event) {
    this.scrollTop = event.nativeEvent.srcElement.scrollTop;
    console.log(`scrollTop: ${this.scrollTop}`);
  }

  determineBestTimeRange() {
    const { firstDate, lastDate } = DA.getLastRequestedTopReferrersTimeRange();
    if (!firstDate || !lastDate) {
      const startOfToday = DateTime.local().startOf('day');
      const twentyNineDays = Duration.fromObject({ days: 29 }); // last 30 days
      const twentyNineDaysAgo = startOfToday.minus(twentyNineDays);
      return { firstDate: twentyNineDaysAgo, lastDate: startOfToday };
    } else {
      return { firstDate, lastDate };
    }
  }

  determineBestMaxNToShow() {
    const savedN = DA.getLastRequestedTopReferrersMaxNToShow();
    return savedN || 5;
  }

  computeTimeRange() {
    const now = DateTime.local();
    const startOfToday = now.startOf('day');
    const startOfTomorrow = now.plus(Duration.fromObject({ days: 1 })).startOf('day');

    switch (this.state.timeRange) {
      case 'All Time':
        return { firstDate: DateTime.fromMillis(0), lastDate: startOfToday };
      case 'Last 30 Days':
        const thirtyDaysAgo = startOfToday.minus(Duration.fromObject({ days: 30 }));
        return { firstDate: thirtyDaysAgo, lastDate: startOfToday };
      case 'Last Quarter':
        const startOfThisQuarter = startOfToday.startOf('quarter');
        const dateInLastQuarter = startOfThisQuarter.minus(Duration.fromObject({ days: 2 }));
        const startOfLastQuarter = dateInLastQuarter.startOf('quarter');
        const endOfLastQuarter = dateInLastQuarter.endOf('quarter').startOf('day');
        return { firstDate: startOfLastQuarter, lastDate: endOfLastQuarter };
      case 'Last 6 Months':
        const sixMonths = Duration.fromObject({ months: 6 });
        const sixMonthsAgo = startOfTomorrow.minus(sixMonths);
        return { firstDate: sixMonthsAgo, lastDate: startOfToday };
      case 'Last 12 Months':
        const twelveMonths = Duration.fromObject({ months: 12 });
        const twelveMonthsAgo = startOfTomorrow.minus(twelveMonths);
        return { firstDate: twelveMonthsAgo, lastDate: startOfToday };
      default:
        const thirtyDaysAgo2 = startOfToday.minus(Duration.fromObject({ days: 30 }));
        return { firstDate: thirtyDaysAgo2, lastDate: startOfToday };
    }
  }

  fetchRawReferralStats() {
    this.setState({ dataReady: false });
    /*
    const cachedResult = DA.getCachedTopReferrersResultsForTimeRange(this.state.timeRange);
    if (cachedResult) {
      this.props.addTopReferrersInfo({
        timeRange: this.state.timeRange,
        maxNToShow: this.state.maxNToShow,
        results: cachedResult,
      });
      this.rawStats = this.convertJsonToRawStats(cachedResult);
      this.setState({ dataReady: true });
      return;
    }
    const { firstDate, lastDate } = this.computeTimeRange();
    */

    const { firstDate, lastDate, maxNToShow } = this.state;
    const firmId = DA.getFirmId();
    const firstDateIso = firstDate.toISODate();
    const lastDateIso = lastDate.toISODate();
    const uri = `/api/referral/toFirm/${firmId}/start/${firstDateIso}/end/${lastDateIso}/maxNToShow/${maxNToShow}`;
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (json) => {
      /*
      this.props.addTopReferrersInfo({
        timeRange: this.state.timeRange,
        maxNToShow: this.state.maxNToShow,
        results: json,
      });
      */
      this.props.setTopReferrersInfo({
        firstDate: firstDateIso,
        lastDate: lastDateIso,
        maxNToShow: this.state.maxNToShow,
      });
      this.rawStats = this.convertJsonToRawStats(json);
      this.setState({ dataReady: true });
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  convertJsonToRawStats(json) {
    /*
     * In:
     * [
     *   {
        public int ReferrerFirmId { get; set; }
        public long ContactDate { get; set; }
        public string CommunicationType { get; set; }
        public int ReferreeId { get; set; }
        public int ReferringPersonId { get; set; }
        public int ReferringOrgId { get; set; }
        public int ClientContactPersonId { get; set; }
        public string ClientContactRelationship { get; set; }
        public int ReceivingUserPersonId { get; set; }
        public int ReceivingOrgId { get; set; }
        public int CampaignId { get; set; }
        public string SalesStage { get; set; }
        public List<string> SubstanceNameStarts { get; set; } = new List<string>();
     *   }, <more of the same>
     * ]
     *
     * Out:
     * [
     *   {
     *     referrerSource: 'California Hospital', (or 'Internet Search' or 'Clients/Alumni', etc.)
     *     idNamePairs: [
     *       {id: 723, name: 'Joan Jett'},
     *       {id: 802, name: 'George Jetson'},
     *       {id: 998, name: 'John Jackson' },
     *     ],
     *   }, <more of the same>
     * ]
     */

    /*
    const firmIdsSet = new Set();
    json.forEach(apiStruct => { if (apiStruct.referringOrgId) { firmIdsSet.add(apiStruct.referringOrgId) } });
    const firmIds = Array.from(firmIdsSet);
    return firmIds.map(referringOrgIdPair => {
      return this.statsObjectForReferringOrg(referringOrgIdPair, json)
    });
    */
    return json;
  }

  referreeIdsForReferringOrg(referringOrgId, apiStructs) {
    const apiStructsFromOrg = apiStructs.filter((apiStruct) => apiStruct.referringOrgId === referringOrgId);
    return apiStructsFromOrg.map((apiStruct) => apiStruct.referreeId);
  }

  computeDataFromRawStats() {
    const firstRow = [
      '', // should be 'Conmpany', but we don't want to show that in the UI
      'Referral Count',
      {
        type: 'string',
        role: 'tooltip',
        p: { html: true /* , position: "relative !important", top: "50px !important", color: "#00cc00"*/ },
      },
    ];
    let rows = [firstRow];
    this.rawStats.forEach((firmObj) => {
      const { referrerSource: firmName, idNamePairs } = firmObj;
      const count = idNamePairs.length;
      const selectorCode = this.selectorCodeForReferrees(idNamePairs);
      rows = [...rows, [firmName, count, selectorCode]];
    });
    return rows;

    /*
    return [
      firstRow,
      ['Sam\'s Serious Seminars', 10],
      ['Terri\s Terrible Treatment', 8],
      ['Ray\'s Random Recovery', 7],
      ['Rhonda\'s Rad Rehab', 6],
      ['Joanie\'s Joyful Junkets', 2],
      ['Andy\'s Anonymous Awareness', 1]
    ];
    */
  }

  computeRechartsDataFromRawData() {
    const lines = this.rawStats.map((firmObj) => {
      const { referrerSource: name, idNamePairs } = firmObj;
      const count = idNamePairs.length;
      return { name, count };
    });

    // This should produce an array of arrays index by bar number (dicey!)
    const refsPerFirm = this.rawStats.map((firmObj) => {
      return firmObj.idNamePairs;
    });

    // const minLinesToShow = 12;
    const minLinesToShow = 0;
    if (minLinesToShow > lines.length) {
      // This is a quick hack to supply more lines for visual testing
      let dupedLines = [];
      let dupedRefs = [];
      while (dupedLines.length < 12) {
        dupedLines = [...dupedLines, ...lines];
        dupedRefs = [...dupedRefs, ...refsPerFirm];
      }
      this.refereesPerFirm = dupedRefs;
      return dupedLines;
    } else {
      this.refereesPerFirm = refsPerFirm;
      return lines;
    }
  }

  linkDivStyle = [
    'text-align:center',
    ';padding:6px',
    ';white-space:nowrap',
    ';font-size:120%',
    ';font-weight:500',
  ].join('');

  selectorCodeForReferrees(idNamePairs) {
    const linksCode = idNamePairs.map((idNamePair) => this.selectorCodeForReferree(idNamePair)).join('');
    return '<div style="' + this.linkDivStyle + '">' + linksCode + '</div>';
  }

  linkStyle = ['padding:2px', ';cursor:pointer', ';text-decoration:underline'].join('');

  selectorCodeForReferree(idNamePair) {
    const { id, name } = idNamePair;
    return (
      "<div onClick=\"const current = location.href; const slashLoc = current.lastIndexOf('/'); const root = current.substring(0, slashLoc + 1); const newLoc = root + 'showClient?personId=" +
      id +
      '\'; location.href = newLoc;" style="' +
      this.linkStyle +
      '">' +
      name +
      '</div>'
    );
  }

  /*
  setTimeRange(event) {
    this.setState({ timeRange: event.target.value }, () => this.fetchRawReferralStats());
  }
  */

  setFirstDate(newDate) {
    this.setState({ firstDate: newDate }, () => this.fetchRawReferralStats());
  }

  setLastDate(newDate) {
    this.setState({ lastDate: newDate }, () => this.fetchRawReferralStats());
  }

  setMaxNToShow(event) {
    this.setState({ maxNToShow: event.target.value }, () => this.fetchRawReferralStats());
  }

  // Copied content starts here

  yAxisLeftTick(yAndPayload) {
    const {
      y,
      payload: { value },
    } = yAndPayload;
    const fitter = fittingStr(value);
    return (
      <Text x={0} y={y} textAnchor="start" verticalAnchor="middle">
        {fitter}
      </Text>
    );
  }

  ctx;

  measureText14HelveticaNeue(text) {
    if (!ctx) {
      ctx = document.createElement('canvas').getContext('2d');
      ctx.font = "12px 'Helvetica Neue";
    }
    return ctx.measureText(this.fittingStr(text)).width;
  }

  handleClickOnReferee(menuLineIndex) {
    // console.log(`clicked p${menuLineIndex + 1} of line ${this.state.hoverLineIndex}`);
    const listOfIdNamePairsForThisFirm = this.refereesPerFirm[this.state.hoverLineIndex];
    const { id, name } = listOfIdNamePairsForThisFirm[menuLineIndex];
    console.log(`clicked on ${name} with ID: ${id}`);
    this.props.setCaseIntendedClientId(id);
    this.setState({ redirectToReferee: true });
  }

  getNameForLineAndItemIndexes(lineIndex, itemIndex) {
    const listOfIdNamePairsForThisFirm = this.refereesPerFirm[lineIndex];
    if (!listOfIdNamePairsForThisFirm) {
      return null;
    }
    const { /* id, */ name } = listOfIdNamePairsForThisFirm[itemIndex];
    return name;
  }

  // Copied content stops here

  render() {
    if (!this.state.dataReady) {
      return <KSpinner />;
    }

    if (this.state.redirectToReferee) {
      return <Redirect to="case" push />;
    }

    const { classes } = this.props;
    const data = this.computeDataFromRawStats();
    const haveData = data.length > 1;

    /*
    const taskCompletionOptions = {
      legend: { position: 'none' },
      bars: 'horizontal',
      // tooltip: { isHtml: true, trigger: "visible"},
      tooltip: {
        position: 'relative !important',
        top: '0 !important',
        right: '-60 !important',
        isHtml: true,
        focusTarget: 'category',
        trigger: 'selection',
      },
    };
    */

    /*
    const chartEvents = [
      {
        eventName: 'select',
        callback({ chartWrapper }) {
          console.log('Selected ', chartWrapper.getChart().getSelection());
        },
      },
    ];
    */

    // copied
    /*
    const maxTextWidth = useMemo(
      () =>
        data.reduce((acc, cur) => {
          const value = cur[xKey];
          const localeVal = value.toLocaleString();
          const fitting = this.ittingStr(localeVal);
          const width = this.measureText14HelveticaNeue(fitting);
          if (width > acc) {
            return width;
          }
          return acc;
        }, 0),
      [data, xKey]
    );
    */
    // copied done

    const maxTextWidth = 100;

    const { hoverLineIndex } = this.state;
    const rechartsData = this.computeRechartsDataFromRawData();
    const nPixelsPerLine = 57;
    const nLinesScrolledUp = this.scrollTop / nPixelsPerLine;
    const apparentLineIndex = hoverLineIndex - nLinesScrolledUp;
    const topOfOverlay = 20 + apparentLineIndex * nPixelsPerLine;

    return (
      <div>
        {haveData ? (
          <div>
            <div
              style={{
                display: hoverLineIndex >= 0 ? 'block' : 'none',
                position: 'absolute',
                zIndex: 100,
                left: 500,
                top: topOfOverlay,
                backgroundColor: 'white',
                border: '1px solid black',
                padding: '2px 8px',
              }}
            >
              {hoverLineIndex >= 0 &&
                this.refereesPerFirm[hoverLineIndex].map((idNamePair, i) => {
                  return (
                    <div
                      key={i}
                      style={{ color: '#3300ff', textDecoration: 'underline', cursor: 'pointer', marginBottom: '4px' }}
                      onClick={() => this.handleClickOnReferee(i)}
                    >
                      {this.getNameForLineAndItemIndexes(hoverLineIndex, i)}
                    </div>
                  );
                })}
            </div>
            <div className={classes.chartHolderDiv} onScroll={(event) => this.handleScroll(event)}>
              <ResponsiveContainer width={'100%'} height={60 * rechartsData.length}>
                <BarChart
                  data={rechartsData}
                  layout="vertical"
                  margin={{ left: maxTextWidth, right: 20 }}
                  onMouseMove={(state) => {
                    if (state) {
                      if (state.isTooltipActive) {
                        if (this.state.hoverLineIndex !== state.activeTooltipIndex) {
                          this.setState({ hoverLineIndex: state.activeTooltipIndex });
                        }
                      } else {
                        this.setState({ hoverLineIndex: -1 });
                      }
                    } else {
                      if (this.state.hoverLineIndex != -1) {
                        this.setState({ hoverLineIndex: -1 });
                      }
                    }
                  }}
                >
                  <XAxis axisLine={true} type="number" />
                  <YAxis dataKey="name" type="category" axisLine={true} tickLine={false} tick={this.yAxisLeftTick} />
                  <Bar dataKey="count" minPointSize={2} barSize={32}>
                    {rechartsData.map((d, idx) => {
                      return <Cell key={d['name']} fill="#A2BBD2" />;
                    })}
                  </Bar>
                  {true && (
                    <Tooltip isAnimationActive={false} cursor={false} offset={-20} content={<CustomizedTooltip />} />
                  )}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <div>
            <div className={classes.bufferDiv} />
            <div className={classes.notificationDiv}>No data in time range</div>
            <div className={classes.bufferDiv} />
          </div>
        )}
        <Grid container>
          <Grid item xs={9}>
            <DateRangePicker
              startLabel="Start Date"
              endLabel="End Date"
              startValue={this.state.firstDate}
              endValue={this.state.lastDate}
              onStartValueChange={(newDate) => this.setFirstDate(newDate)}
              onEndValueChange={(newDate) => this.setLastDate(newDate)}
            />
          </Grid>
          <Grid item xs={3}>
            <KSelect
              fullWidth={true}
              label="Show at most"
              liveOptions={['5 Referral Sources', '10 Referral Sources', '20 Referral Sources']}
              liveValues={[5, 10, 20]}
              value={this.state.maxNToShow}
              onChange={(event) => this.setMaxNToShow(event)}
            />
          </Grid>
        </Grid>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({});

const actionProducers = {
  setTopReferrersInfo,
  setCaseIntendedClientId,
  // setIntendedClientId,
};

export default connect(mapStateToProps, actionProducers)(withStyles(localStyles)(TopReferralFirmsChart));

/*
 * When we showed named ranges we had this code:
          <div className={classes.selectDiv}>
            <KSelect
              fullWidth={true}
              label="Date Range"
              liveOptions={['All Time', 'Last 30 Days', 'Last Quarter', 'Last 6 Months', 'Last 12 Months']}
              value={this.state.timeRange}
              onChange={(event) => this.setTimeRange(event)}
            />
          </div>
 */
